import React, { useState, useRef } from 'react';
import Formsy from 'formsy-react';
import {
  RemoteChipSelectFormsy,
  SelectFormsy,
  TextFieldFormsy,
  ChipSelectFormsy,
} from 'components/formsy';

import {
  CommodityCodeDto,
  ETransportationMode,
  TransactionScoringRequestDto,
  TransportationScoringRequestDto,
  TransportedProduct
} from 'models/dtoTypes';
import { SearchHsCode } from 'services/openapiClient';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { get, isEmpty } from 'lodash';
import { ScoreNewTransaction } from 'services/transaction';
import Notice from 'kosmos/components/Notice';
import { useHistory } from 'react-router';
import { Breadcrumbs, Grid, Paper, Stack, Typography } from '@mui/material';
import { countryOptions } from 'utils/countries';
import Link from 'components/Link';
import { transformOptionData } from 'utils/form';
import CompanyPicker from './_companyPicker';

const PREFIX = 'NewTransactionPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
  input: `${PREFIX}-input`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  paddingBottom: theme.spacing(10),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    }
  },
  [`& .${classes.input}`]: {
    width: '100%',
  }
}));


export interface IField {
  required?: boolean;
  label: string;
  widget: string;
  name: string;
  description?: string;
  options?: { alias: string; label: string; }[];
  placeholder?: string;
}

export interface ISchema {
  fields: IField[];
}

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

function NewTransactionPage(): JSX.Element {
  const history = useHistory();
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const formRef = useRef<Formsy>(null);
  const [loading, setLoading] = useState(false);

  const validatePostBody = (formData: Record<string, unknown>): boolean => {
    const fields = [
      'financialProduct',
      'financialProductReference',
      'transportationMode',
      'originLocode',
      'destinationLocode',
      'hsCode',
      'weightInKg',
      'countryOfOrigin',
      'buyerId',
      'sellerId',
    ];

    const invalidFields: string[] = [];

    fields.forEach(field => {
      if (isEmpty(formData[field])) {
        invalidFields.push(field);
      }
    });

    if (invalidFields?.length) {
      return false;
    }
    return true;
  };


  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const generatePostData = (transformed: Record<string, unknown>) : TransactionScoringRequestDto => {
    const {
      hsCode,
      weightInKg,
      countryOfOrigin,
      transportationMode,
      originLocode,
      destinationLocode,
      financialProduct,
      financialProductReference,
      buyerId,
      sellerId,
    } = transformed;
    const formattedHSCode = ((hsCode as string) || '').split(':')[1];
    const productObj: TransportedProduct = {
      hsCode: formattedHSCode,
      weightInKg: weightInKg as number,
      countryOfOrigin: countryOfOrigin as string
    };
    const transportObj: TransportationScoringRequestDto = {
      transportationMode: transportationMode as ETransportationMode,
      originLocode: originLocode as string,
      destinationLocode: destinationLocode as string,
      products: [productObj]
    };
    const requestBody: TransactionScoringRequestDto = {
      financialProduct: financialProduct as string,
      financialProductReference: financialProductReference as string,
      buyerId: buyerId as string,
      sellerId: sellerId as string,
      transportations: [transportObj]
    };
    return requestBody;
  };

  const handleCreate = async (model: FormData) => {
    const transformed = transformOptionData(model as Record<string, unknown>);
    const flag = validatePostBody(transformed);
    const body = generatePostData(transformed);
    console.log('formData', transformed);
    if (!flag) {
      return setNoticeData({
        message: 'Please fill in all the fields to submit for scoring.',
        severity: 'error',
      });
    }
    setLoading(true);
    try {
      await ScoreNewTransaction(body);
      handleClose();
    } catch (e) {
      handleError(e);
    }
    setLoading(false);
  };

  const handleClose = () => {
    history.push('/transactions');
  };

  const renderCardContent = () => (
    <Formsy
      ref={formRef}
      disabled={true}
      onSubmit={(model: FormData) => handleCreate(model)}
      // onValid={enableButton}
      // onChange={handleChange}
      // onInvalid={disableButton}
      className="form-container flex flex-col justify-center"
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>Financial Product</Box>
          </Typography>
          <Paper elevation={0} >
            <SelectFormsy
              className={classes.input}
              required={true}
              disabled={loading}
              name="financialProduct"
              label=""
              placeholder="Financial Product"
              variant="outlined"
              options={[
                { alias: 'Trade Finance', label: 'Trade Finance' },
                { alias: 'Supply Chain Finance', label: 'Supply Chain Finance' },
                { alias: 'Trade Loan', label: 'Trade Loan' },
                { alias: 'Receivables Finance', label: 'Receivables Finance' },
              ]}
            />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>Financial Product Reference</Box>
          </Typography>
          <Paper elevation={0} >
            <TextFieldFormsy
              className={classes.input}
              disabled={loading}
              required={true}
              type="text"
              name="financialProductReference"
              label=""
            />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>Buyer Information</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2} >
            <CompanyPicker name={'buyerId'} />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>Seller Information</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2} >
            <CompanyPicker name={'sellerId'} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>Product Information</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2} >
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>Product (HS Code + Description)</Box>
                </Typography>
                <Paper elevation={0} >
                  <RemoteChipSelectFormsy
                    className={classes.input}
                    name="hsCode"
                    disabled={loading}
                    required={true}
                    onSearchOptions={SearchHsCode}
                    optionRenderer={
                      ({ id, commodity }: CommodityCodeDto) => ({
                        alias: id,
                        label: ` (${(id || '').toUpperCase()}) ${commodity as string}`
                      })
                    }
                    variant="outlined"
                    isMulti={false}
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>Weight (in Kg)</Box>
                </Typography>
                <Paper elevation={0} >
                  <TextFieldFormsy
                    className={classes.input}
                    disabled={loading}
                    required={true}
                    type="number"
                    name="weightInKg"
                    label=""
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>Country of Origin</Box>
                </Typography>
                <Paper elevation={0} >
                  <ChipSelectFormsy
                    name="countryOfOrigin"
                    className={classes.input}
                    required={true}
                    textFieldProps={{
                      variant: 'outlined'
                    }}
                    variant="outlined"
                    options={countryOptions()}
                    isMulti={false}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>Transportation Information</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2} >
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>Mode of Transport</Box>
                </Typography>
                <Paper elevation={0} >
                  <SelectFormsy
                    className={classes.input}
                    required={true}
                    disabled={loading}
                    name="transportationMode"
                    label=""
                    placeholder="transportationMode"
                    variant="outlined"
                    options={[
                      { alias: 'Maritime', label: 'Maritime' },
                      { alias: 'Rail', label: 'Rail' },
                      { alias: 'Road', label: 'Road' },
                      { alias: 'Air', label: 'Air' },
                    ]}
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>Origin</Box>
                </Typography>
                <Paper elevation={0} >
                  <ChipSelectFormsy
                    name="originLocode"
                    className={classes.input}
                    required={true}
                    textFieldProps={{
                      variant: 'outlined'
                    }}
                    variant="outlined"
                    options={countryOptions()}
                    isMulti={false}
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>Destination</Box>
                </Typography>
                <Paper elevation={0} >
                  <ChipSelectFormsy
                    name="destinationLocode"
                    className={classes.input}
                    required={true}
                    textFieldProps={{
                      variant: 'outlined'
                    }}
                    variant="outlined"
                    options={countryOptions()}
                    isMulti={false}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper style={{ padding: 0 }} elevation={0} >
            <Stack direction="row" spacing={2}>
              <Button
                disabled={loading}
                variant="outlined"
                onClick={handleClose}
                color="primary"
              >
                Cancel
              </Button>
              <Button
                disabled={loading}
                type="submit"
                variant="contained"
                color="primary"
                autoFocus
              >
                {'Submit For Scoring'}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Formsy>
  );

  return (
    <Root>
      <Breadcrumbs aria-label="breadcrumb">
        <Link
          to="/"
          text="Home"
        />
        <Link to={'/transactions'} text={'Transactions'} />
        <Typography color="text.primary">New</Typography>
      </Breadcrumbs>
      <div className={classes.pageHeader}>
        <h2>Score New Transaction</h2>
      </div>
      {renderCardContent()}
      {loading && <LinearProgress />}
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}

export default NewTransactionPage;
